// src/components/schedule/delivery/DeliveryCard.js
// Memoized delivery card with dual drag handles (date + driver).
// Extracted from the main page to prevent recreation on every parent render.

import React, { memo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import {
    FiHash, FiUser, FiCalendar, FiUsers, FiMove, FiEdit3,
    FiClock, FiLayers, FiTruck, FiInfo, FiCheckCircle,
    FiDollarSign, FiTool,
} from 'react-icons/fi';
import { CheckCircle, MapIcon } from 'lucide-react';
import { IoWarning } from 'react-icons/io5';
import { hasPermission } from '../../../lib/permissions';
import {
    getQuoteTypeStyle,
    getMainStatusColor,
    getEffectiveProductionStatus,
    CardGeneralStatusText,
    CardGeneralStatusStyle,
    getStatusDot,
} from './deliveryUtils';

// ─── InfoTooltip ──────────────────────────────────────────────────────────────

const InfoTooltip = memo(({ assignment, hasDate, hasDriver, driverName, isDeliveryCompleted, delivery }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, showAbove: false });
    const [triggerEl, setTriggerEl] = useState(null);

    useEffect(() => {
        if (!isHovered || !triggerEl) return;
        const rect = triggerEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const showAbove = spaceBelow < 280 && rect.top > 280;
        setTooltipPos({
            x: rect.left,
            y: showAbove ? rect.top - 8 : rect.bottom + 8,
            showAbove,
        });
    }, [isHovered, triggerEl]);

    const overflow = tooltipPos.x > window.innerWidth - 320;

    const TooltipContent = () => (
        <div
            className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
            style={{
                left: tooltipPos.x,
                top: tooltipPos.y,
                transform: `${overflow ? 'translateX(-100%)' : ''} ${tooltipPos.showAbove ? 'translateY(-100%)' : ''}`.trim() || undefined,
            }}
        >
            {/* Arrow */}
            <div
                className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPos.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'}`}
                style={{
                    [tooltipPos.showAbove ? 'bottom' : 'top']: '-6px',
                    left: overflow ? 'calc(100% - 20px)' : '12px',
                }}
            />

            <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 text-sm mb-2">Job Information</h4>

                {/* Expected Finish */}
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <FiCalendar className="w-3.5 h-3.5 text-blue-600" />
                        <span className="font-medium">Expected Finish</span>
                    </div>
                    <span className="text-gray-700 font-medium">
                        {hasDate
                            ? new Date(assignment.quote_info.expected_finish_date)
                                .toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })
                            : 'Not set'}
                    </span>
                </div>

                {/* Design */}
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <FiLayers className="w-3.5 h-3.5 text-blue-600" />
                        <span className="font-medium">Design Status</span>
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quote_info?.design_status)}`} />
                    </div>
                    <span className="text-gray-700 capitalize">
                        {assignment.quote_info?.design_status?.replace(/_/g, ' ') || 'N/A'}
                    </span>
                </div>

                {/* Estimation */}
                {assignment.quote_info?.estimation_status !== 'not_required' && (
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <FiDollarSign className="w-3.5 h-3.5 text-green-600" />
                            <span className="font-medium">Estimation Status</span>
                            <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quote_info?.estimation_status)}`} />
                        </div>
                        <span className="text-gray-700 capitalize">
                            {assignment.quote_info?.estimation_status?.replace(/_/g, ' ') || 'N/A'}
                        </span>
                    </div>
                )}

                {/* Production */}
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <FiTool className="w-3.5 h-3.5 text-purple-600" />
                        <span className="font-medium">Production Status</span>
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quote_info?.production_status)}`} />
                    </div>
                    <span className="text-gray-700 capitalize">
                        {assignment.quote_info?.production_status?.replace(/_/g, ' ') || 'N/A'}
                    </span>
                </div>

                {/* Delivery */}
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <FiTruck className="w-3.5 h-3.5 text-red-600" />
                        <span className="font-medium">Delivery Status</span>
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(delivery?.delivery_status)}`} />
                    </div>
                    <span className="text-gray-700 capitalize">
                        {delivery?.delivery_status?.replace(/_/g, ' ') || 'N/A'}
                    </span>
                </div>

                {/* Driver */}
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                        <FiUsers className="w-3.5 h-3.5 text-teal-600" />
                        <span className="font-medium">Driver</span>
                    </div>
                    <span className="text-gray-700 font-medium">{hasDriver ? driverName : 'Not assigned'}</span>
                </div>

                {/* Time slot */}
                {assignment.quote_info?.delivery_time_slot && (
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <FiClock className="w-3.5 h-3.5 text-orange-600" />
                            <span className="font-medium">Time Slot</span>
                        </div>
                        <span className="text-gray-700 font-medium">{assignment.quote_info.delivery_time_slot}</span>
                    </div>
                )}

                {/* Note */}
                {assignment.quote_info?.delivery_note && (
                    <div className="text-xs border-t border-gray-100 pt-2 mt-2">
                        <div className="flex items-start gap-2">
                            <FiEdit3 className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <span className="font-medium block mb-1">Delivery Note:</span>
                                <span className="text-gray-700 leading-relaxed">{assignment.quote_info.delivery_note}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Completed */}
                {isDeliveryCompleted && (
                    <div className="border-t border-gray-100 pt-2 mt-2 flex items-center gap-2 text-xs">
                        <FiCheckCircle className="w-3.5 h-3.5 text-green-600" />
                        <span className="font-medium text-green-700">Delivery Completed</span>
                    </div>
                )}

                {/* Legend */}
                <div className="border-t border-gray-100 pt-2 mt-2 flex items-center gap-3 text-[10px] text-gray-500">
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500" /><span>Completed</span></div>
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span>In Progress</span></div>
                    <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /><span>Waiting</span></div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <button
                ref={setTriggerEl}
                className="p-1.5 rounded-lg bg-white text-gray-600 hover:bg-gray-100 border border-gray-300 transition-all shadow-md"
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <FiInfo className="w-3.5 h-3.5" />
            </button>
            {isHovered && createPortal(<TooltipContent />, document.body)}
        </>
    );
});
InfoTooltip.displayName = 'InfoTooltip';

// ─── DeliveryCard ─────────────────────────────────────────────────────────────

/**
 * Memoized delivery card with dual drag handles (date + driver).
 *
 * Props:
 *   assignment      — the job/quote object
 *   isUnscheduled   — (optional) renders in unscheduled zone
 *   viewingMode     — hides edit controls when true
 *   userRole        — 'admin' | 'super_admin' | 'driver' | etc.
 *   isMobile        — show mobile move button
 *   companySlug     — for navigate links
 *   onOpenNote      — fn(assignment, quoteType, currentNote, deliveryCount)
 *   onOpenTimeSlot  — fn(assignment, quoteType, currentSlot, deliveryCount)
 *   onMobileMove    — fn(assignment, e)
 */
const DeliveryCard = memo(({
    assignment,
    isUnscheduled = false,
    viewingMode,
    userRole,
    isMobile,
    companySlug,
    onOpenNote,
    onOpenTimeSlot,
    onMobileMove,
}) => {
    const navigate = useNavigate();

    const quoteType         = assignment.quote_info?.quote_type;
    const quoteDeliveryCount = assignment.quote_info?.delivery_count ?? 0;
    const quoteId           = assignment.quote_info?.quoteId || null;

    // Unique draggable ID: use job_quotes UUID for new-system, composite for old-system
    const uniqueCardId = quoteId
        ? `${assignment.id}-${quoteId}`
        : `${assignment.id}-${quoteType}-${quoteDeliveryCount}`;

    const isAdmin = userRole === 'admin' || userRole === 'super_admin';

    // Date drag handle
    const dateHandle = useDraggable({
        id: `${uniqueCardId}-date`,
        data: { assignment, handleType: 'date' },
        disabled: !isAdmin
    });

    // Driver drag handle
    const driverHandle = useDraggable({
        id: `${uniqueCardId}-driver`,
        data: { assignment, handleType: 'driver' },
        disabled: !isAdmin
    });

    const isDragging = dateHandle.isDragging || driverHandle.isDragging;

    // Match the specific delivery record for this quote.
    // isNewSystem is determined by childUniqueId (display name) on the quote, not by whether
    // individual job_deliveries rows happen to have child_id set.  This prevents old-style
    // delivery rows (child_id = null) from being matched for new-system cards via the
    // quote_type fallback, which would show stale/wrong driver info.
    const isNewSystem = !!assignment.quote_info?.childUniqueId;
    const delivery = assignment.job_deliveries?.find(d =>
        isNewSystem
            ? d.child_id === quoteId            // new-system: UUID match only
            : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
    );

    const hasDate             = !!assignment.quote_info?.expected_finish_date;
    const hasDriver           = !!delivery?.assigned_driver_id;
    const driverName          = delivery?.assigned_driver_name || 'Set Driver';
    const isDeliveryCompleted = delivery?.delivery_status === 'delivered';
    const childUniqueId       = assignment.quote_info?.childUniqueId || null;

    const quoteStyle = getQuoteTypeStyle(quoteType);
    const QuoteIcon  = quoteStyle.icon;

    const isDesignCompleted =
        assignment?.quote_info?.design_status === 'completed' ||
        assignment?.quote_info?.design_status === 'not_required';

    const hasAddress = !!(
        assignment?.customer_address &&
        (typeof assignment.customer_address === 'string'
            ? assignment.customer_address.length > 0
            : Object.keys(assignment.customer_address).length > 0)
    );

    const canAssignDriver     = isDesignCompleted && hasAddress;
    const isProductionFinished = getEffectiveProductionStatus(assignment) === 'completed';
    const isReadyToDeliver    = isDesignCompleted && hasAddress && isProductionFinished;

    const notReadyReasons = [
        !isDesignCompleted    && `Design not completed (${assignment?.quote_info?.design_status?.split('_').join(' ')})`,
        !hasAddress           && 'Delivery address is not set',
        !isProductionFinished && 'Production not finished',
    ].filter(Boolean);

    // Enrich quote_info with effective production status for the status bar helpers
    const enrichedQuote = assignment.quote_info
        ? { ...assignment.quote_info, production_status: getEffectiveProductionStatus(assignment) }
        : {};

    return (
        <div
            className={`relative group flex flex-col bg-white border-2 rounded-lg p-3 shadow-sm hover:shadow-md transition-all h-full ${
                isDeliveryCompleted ? 'border-green-300 bg-green-50' : 'border-gray-200'
            } ${isDragging ? 'opacity-50' : ''}`}
        >
            {/* Hover action buttons */}
            {!viewingMode && (
                <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    {/* Note */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenNote(assignment, quoteType, assignment.quote_info?.delivery_note || '', quoteDeliveryCount);
                        }}
                        className={`p-1.5 rounded-lg transition-all shadow-md ${
                            assignment.quote_info?.delivery_note
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                        }`}
                        title={assignment.quote_info?.delivery_note ? 'Edit note' : 'Add note'}
                    >
                        <FiEdit3 className="w-3.5 h-3.5" />
                    </button>

                    {/* Time slot (admin only) */}
                    {hasPermission(userRole, ['super_admin', 'admin']) && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenTimeSlot(assignment, quoteType, assignment.quote_info?.delivery_time_slot, quoteDeliveryCount);
                            }}
                            className={`p-1.5 rounded-lg transition-all shadow-md ${
                                assignment.quote_info?.delivery_time_slot
                                    ? 'bg-purple-500 text-white hover:bg-purple-600'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                            }`}
                            title={assignment.quote_info?.delivery_time_slot ? `Time slot: ${assignment.quote_info.delivery_time_slot}` : 'Set time slot'}
                        >
                            <FiClock className="w-3.5 h-3.5" />
                        </button>
                    )}

                    <InfoTooltip
                        assignment={assignment}
                        hasDate={hasDate}
                        hasDriver={hasDriver}
                        driverName={driverName}
                        isDeliveryCompleted={isDeliveryCompleted}
                        delivery={delivery}
                    />
                </div>
            )}

            {/* Main content */}
            <div className="w-full">
                {/* Status bar */}
                <div className={`w-full h-[2px] rounded-full mb-3 relative ${CardGeneralStatusStyle(delivery || {}, enrichedQuote, 'bar')} ${getMainStatusColor(assignment.main_status)}`}>
                    <span className={`absolute -top-[6.75px] right-0 text-[11px] bg-white pl-2 font-semibold ${CardGeneralStatusStyle(delivery || {}, enrichedQuote, 'text')}`}>
                        {CardGeneralStatusText(assignment)}
                    </span>
                </div>

                {/* Job ID row */}
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span
                            className="font-semibold text-gray-900 text-sm truncate cursor-pointer hover:text-teal-600 hover:underline transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (childUniqueId && assignment.quote_info?.quoteId) {
                                    navigate(`/${companySlug}/assignment/${assignment.id}/${assignment.quote_info.quoteId}`);
                                } else {
                                    navigate(`/${companySlug}/assignment/${assignment.id}`);
                                }
                            }}
                        >
                            {childUniqueId ? childUniqueId : assignment.unique_id}
                        </span>
                        {childUniqueId && (
                            <span className="text-[11px] text-gray-400 font-normal flex-shrink-0">
                                (PRN: {assignment.unique_id})
                            </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-[1px] rounded-full text-[11px] font-medium ${quoteStyle.bgColor} ${quoteStyle.textColor} flex-shrink-0`}>
                            <QuoteIcon className="w-[11px] h-[11px]" />
                            {quoteStyle.label}
                        </span>
                        {assignment.quote_info?.delivery_count > 0 && (
                            <span className="inline-flex items-center px-2 py-[1px] rounded-full text-[11px] font-medium bg-orange-100 text-orange-800 flex-shrink-0">
                                Rescheduled
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {(assignment.urgent_quote || assignment.urgent_design) && (
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">URGENT</span>
                        )}
                        {isMobile && isAdmin && (
                            <button
                                onClick={(e) => onMobileMove(assignment, e)}
                                className="p-1 text-gray-400 hover:text-teal-600 transition-colors"
                            >
                                <FiMove className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Customer */}
                <div className="flex items-center gap-2">
                    <FiUser className="w-3 h-3 text-teal-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate font-medium">{assignment.customer_name}</span>
                </div>

                {/* Address */}
                {assignment?.customer_address && (
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.customer_address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-green-700 hover:text-green-800 flex items-center gap-1 mt-1"
                    >
                        <MapIcon className="w-3 h-3 flex-shrink-0" />
                        {assignment.customer_address}
                    </a>
                )}

                {/* Drag handles + delivery info row (hidden in viewing mode) */}
                {!viewingMode && (
                    <>
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100 mt-3">
                            {/* Date handle */}
                            <div
                                ref={dateHandle.setNodeRef}
                                {...dateHandle.attributes}
                                {...dateHandle.listeners}
                                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded select-none touch-none ${
                                    hasDate ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'
                                } cursor-grab active:cursor-grabbing hover:shadow-md transition-all`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <FiCalendar className="w-3 h-3" />
                                <span className="text-xs font-medium">
                                    {hasDate
                                        ? new Date(assignment.quote_info.expected_finish_date)
                                            .toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
                                        : 'Set Date'}
                                </span>
                            </div>

                            {/* Driver handle */}
                            <div
                                ref={canAssignDriver ? driverHandle.setNodeRef : null}
                                {...(canAssignDriver ? driverHandle.attributes : {})}
                                {...(canAssignDriver ? driverHandle.listeners : {})}
                                className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded select-none touch-none ${
                                    !canAssignDriver
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                                        : hasDriver
                                            ? 'bg-blue-100 text-blue-700 cursor-grab active:cursor-grabbing hover:shadow-md'
                                            : 'bg-gray-100 text-gray-600 cursor-grab active:cursor-grabbing hover:shadow-md'
                                } transition-all`}
                                onClick={(e) => e.stopPropagation()}
                                title={
                                    !hasAddress        ? 'Address must be set before assigning driver'
                                    : !isDesignCompleted ? 'Design must be completed before assigning driver'
                                    : ''
                                }
                            >
                                <FiUsers className="w-3 h-3" />
                                <span className="text-xs font-medium truncate">{driverName}</span>
                            </div>
                        </div>

                        <div className="mt-2 space-y-2">
                            {/* Selected units */}
                            {assignment.quote_info?.selected_units?.length > 0 && (
                                <div className="flex flex-wrap gap-1 px-2 py-1.5">
                                    {assignment.quote_info.selected_units.map(unitNum => (
                                        <span key={unitNum} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            Unit {unitNum}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="flex flex-row w-full gap-2">
                                {/* Time slot chip */}
                                <div className={`flex w-1/2 items-center gap-2 px-2 py-0.5 rounded-lg border ${
                                    assignment.quote_info?.delivery_time_slot ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <FiClock className={`w-3 h-3 flex-shrink-0 ${assignment.quote_info?.delivery_time_slot ? 'text-purple-600' : 'text-gray-400'}`} />
                                    <span className={`text-xs font-medium ${assignment.quote_info?.delivery_time_slot ? 'text-purple-900' : 'text-gray-500'}`}>
                                        {assignment.quote_info?.delivery_time_slot || 'Time slot not set'}
                                    </span>
                                </div>

                                {/* Ready to deliver */}
                                <div className="relative w-1/2">
                                    <div className={`group/statusbar flex items-center py-0.5 px-2 gap-2 rounded-lg border text-xs font-semibold cursor-default ${
                                        isReadyToDeliver ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                                    }`}>
                                        {isReadyToDeliver ? <CheckCircle className="w-3.5 h-3.5" /> : <IoWarning className="w-3.5 h-3.5" />}
                                        {isReadyToDeliver ? 'Ready to deliver' : 'Not ready'}

                                        {!isReadyToDeliver && notReadyReasons.length > 0 && (
                                            <div className="absolute -bottom-14 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover/statusbar:opacity-100 transition-opacity pointer-events-none z-[9999] shadow-lg">
                                                <div className="p-3 space-y-1">
                                                    {notReadyReasons.map((reason, idx) => (
                                                        <div key={idx} className="flex items-start gap-2">
                                                            <IoWarning className="w-3 h-3 mt-0.5 text-red-400 flex-shrink-0" />
                                                            <span>{reason}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Note */}
                            <div className={`flex items-start gap-2 px-2 py-1.5 rounded-lg ${
                                assignment.quote_info?.delivery_note ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                            }`}>
                                <FiEdit3 className={`w-3 h-3 flex-shrink-0 mt-0.5 ${assignment.quote_info?.delivery_note ? 'text-blue-600' : 'text-gray-400'}`} />
                                <span className={`text-xs line-clamp-2 ${assignment.quote_info?.delivery_note ? 'text-blue-900' : 'text-gray-500 italic'}`}>
                                    {assignment.quote_info?.delivery_note || 'No delivery note'}
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});

DeliveryCard.displayName = 'DeliveryCard';
export default DeliveryCard;
